import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  FilePlus2,
  ImagePlus,
  LogOut,
  Pencil,
  Trash2,
  Upload,
  X,
  ShieldCheck,
} from "lucide-react";
import { blogRequest, imageUrl } from "../lib/blogApi";

const emptyForm = {
  _id: "",
  title: "",
  slug: "",
  excerpt: "",
  contentHtml: "",
  tagsText: "",
  keywordsText: "",
  pageTitle: "",
  metaDescription: "",
  featureImage: "",
  imagesText: "",
  status: "draft",
};

const TOKEN_KEY = "rankifydev_cms_token";

const slugify = (value = "") =>
  value
    .toLowerCase()
    .trim()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export default function CmsAdmin() {
  const [token, setToken] = useState(
    () => sessionStorage.getItem(TOKEN_KEY) || ""
  );

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [setupKey, setSetupKey] = useState("");
  const [otp, setOtp] = useState("");
  const [challengeToken, setChallengeToken] = useState("");
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [manualEntryKey, setManualEntryKey] = useState("");
  const [authStep, setAuthStep] = useState("credentials");

  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  const authHeaders = () => ({
    Authorization: `Bearer ${token}`,
  });

  const setField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  function clearAuthState() {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setPosts([]);
    setForm(emptyForm);
    setPassword("");
    setSetupKey("");
    setOtp("");
    setChallengeToken("");
    setQrDataUrl("");
    setManualEntryKey("");
    setAuthStep("credentials");
  }

  function logout() {
    clearAuthState();
    setMessage("You have been signed out.");
  }

  async function loadPosts(currentToken = token) {
    const data = await blogRequest("/admin/posts", {
      headers: { Authorization: `Bearer ${currentToken}` },
    });
    setPosts(data.posts || []);
  }

  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    async function fetchPosts() {
      try {
        const data = await blogRequest("/admin/posts", {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!cancelled) setPosts(data.posts || []);
      } catch (err) {
        if (cancelled) return;
        setMessage(err.message || "Unable to load blog posts.");

        if (/session|sign in|two-factor|verified/i.test(err.message || "")) {
          clearAuthState();
        }
      }
    }

    fetchPosts();

    return () => {
      cancelled = true;
    };
  }, [token]);

  function saveTokenAndEnterAdmin(newToken) {
    if (!newToken) {
      setMessage("The server did not return a login token. Check the backend routes.");
      return;
    }

    sessionStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
    setPassword("");
    setSetupKey("");
    setOtp("");
    setChallengeToken("");
    setQrDataUrl("");
    setManualEntryKey("");
    setAuthStep("credentials");
    setMessage("Signed in successfully.");
  }

  async function login(event) {
    event.preventDefault();
    setBusy(true);
    setMessage("");

    try {
      const data = await blogRequest("/admin/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      if (data.setupRequired) {
        setAuthStep("setup");
        setMessage(
          "Authenticator setup is required. Enter the private setup key from your backend .env file."
        );
        return;
      }

      if (data.requiresOtp && data.challengeToken) {
        setChallengeToken(data.challengeToken);
        setAuthStep("otp");
        setMessage("Enter the current 6-digit code from Google Authenticator.");
        return;
      }

      if (data.token) {
        saveTokenAndEnterAdmin(data.token);
        return;
      }

      throw new Error(data.message || "Unexpected login response from the server.");
    } catch (err) {
      setMessage(err.message || "Login failed.");
    } finally {
      setBusy(false);
    }
  }

  async function startAuthenticatorSetup(event) {
    event.preventDefault();
    setBusy(true);
    setMessage("");

    try {
      const data = await blogRequest("/admin/2fa/setup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username,
          password,
          setupKey,
        }),
      });

      setQrDataUrl(data.qrDataUrl || "");
      setManualEntryKey(data.manualEntryKey || "");
      setOtp("");
      setAuthStep("enroll");
      setMessage(
        "Scan the QR code with Google Authenticator, then enter the 6-digit code shown in the app."
      );
    } catch (err) {
      setMessage(err.message || "Could not start authenticator setup.");
    } finally {
      setBusy(false);
    }
  }

  async function enableAuthenticator(event) {
    event.preventDefault();
    setBusy(true);
    setMessage("");

    try {
      const data = await blogRequest("/admin/2fa/enable", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username,
          password,
          setupKey,
          otp: otp.replace(/\s/g, ""),
        }),
      });

      if (!data.token) {
        throw new Error(data.message || "The server did not return a login token.");
      }

      saveTokenAndEnterAdmin(data.token);
    } catch (err) {
      setMessage(err.message || "Could not enable two-factor authentication.");
    } finally {
      setBusy(false);
    }
  }

  async function verifyAuthenticatorCode(event) {
    event.preventDefault();
    setBusy(true);
    setMessage("");

    try {
      const data = await blogRequest("/admin/2fa/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          challengeToken,
          otp: otp.replace(/\s/g, ""),
        }),
      });

      saveTokenAndEnterAdmin(data.token);
    } catch (err) {
      setMessage(err.message || "OTP verification failed.");
    } finally {
      setBusy(false);
    }
  }

  function returnToCredentials() {
    setAuthStep("credentials");
    setOtp("");
    setSetupKey("");
    setChallengeToken("");
    setQrDataUrl("");
    setManualEntryKey("");
    setMessage("");
  }

  function editPost(post) {
    setSlugTouched(true);
    setForm({
      _id: post._id,
      title: post.title || "",
      slug: post.slug || "",
      excerpt: post.excerpt || "",
      contentHtml: post.contentHtml || "",
      tagsText: (post.tags || []).join(", "),
      keywordsText: (post.keywords || []).join(", "),
      pageTitle: post.pageTitle || "",
      metaDescription: post.metaDescription || "",
      featureImage: post.featureImage || "",
      imagesText: (post.images || []).join("\n"),
      status: post.status || "draft",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function uploadImages(fileList, asFeature = false) {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    setUploading(true);
    setMessage("");

    try {
      const body = new FormData();
      files.forEach((file) => body.append("images", file));

      const data = await blogRequest("/admin/upload", {
        method: "POST",
        headers: authHeaders(),
        body,
      });

      const urls = data.urls || [];

      if (asFeature) {
        setField("featureImage", urls[0] || "");
      } else {
        setField(
          "imagesText",
          [form.imagesText.trim(), ...urls].filter(Boolean).join("\n")
        );
      }

      setMessage(`${urls.length} image(s) uploaded to the backend hosting server.`);
    } catch (err) {
      setMessage(err.message || "Image upload failed.");
    } finally {
      setUploading(false);
    }
  }

  async function savePost(event) {
    event.preventDefault();
    setBusy(true);
    setMessage("");

    const payload = {
      title: form.title,
      slug: form.slug || slugify(form.title),
      excerpt: form.excerpt,
      contentHtml: form.contentHtml,
      tags: form.tagsText,
      keywords: form.keywordsText,
      pageTitle: form.pageTitle,
      metaDescription: form.metaDescription,
      featureImage: form.featureImage,
      images: form.imagesText
        .split("\n")
        .map((value) => value.trim())
        .filter(Boolean),
      status: form.status,
    };

    try {
      await blogRequest(
        form._id ? `/admin/posts/${form._id}` : "/admin/posts",
        {
          method: form._id ? "PUT" : "POST",
          headers: {
            ...authHeaders(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      setMessage(
        form.status === "published" ? "Post saved and published." : "Draft saved."
      );
      setForm(emptyForm);
      setSlugTouched(false);
      await loadPosts();
    } catch (err) {
      setMessage(err.message || "Could not save the post.");
    } finally {
      setBusy(false);
    }
  }

  async function deletePost(post) {
    if (!window.confirm(`Delete "${post.title}" permanently?`)) return;

    try {
      await blogRequest(`/admin/posts/${post._id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });

      setPosts((all) => all.filter((item) => item._id !== post._id));

      if (form._id === post._id) {
        setForm(emptyForm);
        setSlugTouched(false);
      }

      setMessage("Post deleted.");
    } catch (err) {
      setMessage(err.message || "Could not delete the post.");
    }
  }

  const inputClass =
    "mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 font-normal outline-none focus:border-brand focus:ring-2 focus:ring-brand/10";

  if (!token) {
    return (
      <div className="min-h-screen bg-[hsl(var(--brand-navy))] px-4 py-12 sm:py-20">
        <div className="mx-auto max-w-md rounded-3xl bg-white p-7 shadow-2xl sm:p-9">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-semibold text-brand"
          >
            <ArrowLeft size={16} /> Back to website
          </Link>

          <div className="mt-8 flex items-center gap-2 text-xs font-bold uppercase tracking-[.2em] text-brand">
            <ShieldCheck size={17} /> RankifyDev CMS Security
          </div>

          <h1 className="mt-2 text-3xl font-bold text-[hsl(var(--brand-navy))]">
            {authStep === "otp"
              ? "Verify your identity"
              : authStep === "setup"
                ? "Secure your CMS"
                : authStep === "enroll"
                  ? "Connect authenticator"
                  : "Sign in to publish"}
          </h1>

          <p className="mt-3 text-sm leading-6 text-slate-600">
            {authStep === "otp"
              ? "Enter the current 6-digit code from your Google Authenticator app."
              : authStep === "setup"
                ? "Enter the private setup key stored in backend/.env. Your username and password alone cannot register an authenticator."
                : authStep === "enroll"
                  ? "Scan this QR code using Google Authenticator. Keep the setup key private."
                  : "Sign in with your CMS credentials. Two-factor authentication is required before the admin panel opens."}
          </p>

          {authStep === "credentials" && (
            <form onSubmit={login} className="mt-7 space-y-4">
              <label className="block text-sm font-semibold text-slate-700">
                Username
                <input
                  autoComplete="username"
                  required
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  className={inputClass}
                />
              </label>

              <label className="block text-sm font-semibold text-slate-700">
                Password
                <input
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className={inputClass}
                />
              </label>

              {message && (
                <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
                  {message}
                </p>
              )}

              <button
                disabled={busy}
                className="w-full rounded-xl bg-brand px-5 py-3 font-semibold text-white transition hover:opacity-90 disabled:opacity-60"
              >
                {busy ? "Checking..." : "Continue"}
              </button>
            </form>
          )}

          {authStep === "setup" && (
            <form onSubmit={startAuthenticatorSetup} className="mt-7 space-y-4">
              <label className="block text-sm font-semibold text-slate-700">
                Private setup key
                <input
                  type="password"
                  autoComplete="off"
                  required
                  value={setupKey}
                  onChange={(event) => setSetupKey(event.target.value)}
                  className={inputClass}
                  placeholder="Paste CMS_TOTP_SETUP_KEY"
                />
              </label>

              {message && (
                <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
                  {message}
                </p>
              )}

              <button
                disabled={busy}
                className="w-full rounded-xl bg-brand px-5 py-3 font-semibold text-white disabled:opacity-60"
              >
                {busy ? "Preparing QR code..." : "Set up authenticator"}
              </button>

              <button
                type="button"
                onClick={returnToCredentials}
                className="w-full rounded-xl border px-5 py-3 text-sm font-semibold"
              >
                Back
              </button>
            </form>
          )}

          {authStep === "enroll" && (
            <form onSubmit={enableAuthenticator} className="mt-7 space-y-4">
              {qrDataUrl && (
                <div className="flex justify-center rounded-xl border bg-white p-4">
                  <img
                    src={qrDataUrl}
                    alt="Google Authenticator enrollment QR code"
                    className="h-52 w-52"
                  />
                </div>
              )}

              {manualEntryKey && (
                <div className="rounded-xl bg-slate-50 p-3">
                  <p className="text-xs font-semibold text-slate-600">
                    Manual setup key (keep private)
                  </p>
                  <p className="mt-1 break-all font-mono text-sm">{manualEntryKey}</p>
                </div>
              )}

              <label className="block text-sm font-semibold text-slate-700">
                6-digit authenticator code
                <input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9 ]{6,8}"
                  maxLength={8}
                  required
                  value={otp}
                  onChange={(event) =>
                    setOtp(event.target.value.replace(/[^0-9 ]/g, ""))
                  }
                  className={inputClass}
                  placeholder="123456"
                />
              </label>

              {message && (
                <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
                  {message}
                </p>
              )}

              <button
                disabled={busy}
                className="w-full rounded-xl bg-brand px-5 py-3 font-semibold text-white disabled:opacity-60"
              >
                {busy ? "Verifying..." : "Verify and enable 2FA"}
              </button>

              <p className="text-xs leading-5 text-slate-500">
                Do not share the QR code or manual setup key. Save the authenticator
                in a secure place before finishing setup.
              </p>
            </form>
          )}

          {authStep === "otp" && (
            <form onSubmit={verifyAuthenticatorCode} className="mt-7 space-y-4">
              <label className="block text-sm font-semibold text-slate-700">
                Google Authenticator code
                <input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9 ]{6,8}"
                  maxLength={8}
                  required
                  value={otp}
                  onChange={(event) =>
                    setOtp(event.target.value.replace(/[^0-9 ]/g, ""))
                  }
                  className={inputClass}
                  placeholder="123456"
                />
              </label>

              {message && (
                <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
                  {message}
                </p>
              )}

              <button
                disabled={busy}
                className="w-full rounded-xl bg-brand px-5 py-3 font-semibold text-white disabled:opacity-60"
              >
                {busy ? "Verifying..." : "Verify and sign in"}
              </button>

              <button
                type="button"
                onClick={returnToCredentials}
                className="w-full rounded-xl border px-5 py-3 text-sm font-semibold"
              >
                Back to login
              </button>
            </form>
          )}

          <p className="mt-5 text-xs leading-5 text-slate-500">
            CMS authentication is verified by the backend. Never share your
            backend .env file, setup key, QR code, or authenticator code.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 border-b border-black/5 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-brand">
              RankifyDev
            </div>
            <h1 className="text-xl font-bold text-[hsl(var(--brand-navy))]">
              Blog CMS
            </h1>
          </div>

          <div className="flex items-center gap-3">
            <Link to="/blog" className="hidden text-sm font-semibold text-brand sm:block">
              View blog
            </Link>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold"
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-7xl gap-6 px-4 py-7 sm:px-6 lg:grid-cols-[minmax(0,1.5fr)_minmax(280px,.8fr)]">
        <section className="rounded-2xl border border-black/5 bg-white p-5 shadow-sm sm:p-7">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-brand">
                {form._id ? "Edit article" : "Create article"}
              </p>
              <h2 className="mt-1 text-2xl font-bold text-[hsl(var(--brand-navy))]">
                {form._id ? "Update blog post" : "New blog post"}
              </h2>
            </div>
            {form._id && (
              <button
                type="button"
                onClick={() => {
                  setForm(emptyForm);
                  setSlugTouched(false);
                }}
                className="inline-flex items-center gap-1 text-sm text-slate-500"
              >
                <X size={15} /> Cancel edit
              </button>
            )}
          </div>

          {message && (
            <div className="mt-5 rounded-xl border border-brand/10 bg-lilac p-3 text-sm text-[hsl(var(--brand-navy))]">
              {message}
            </div>
          )}

          <form onSubmit={savePost} className="mt-6 space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block text-sm font-semibold">
                Post title *
                <input
                  required
                  maxLength={180}
                  value={form.title}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      title: event.target.value,
                      slug: slugTouched
                        ? current.slug
                        : slugify(event.target.value),
                      pageTitle: current.pageTitle || event.target.value,
                    }))
                  }
                  className="cms-input"
                />
              </label>

              <label className="block text-sm font-semibold">
                URL slug *
                <input
                  required
                  value={form.slug}
                  onChange={(event) => {
                    setSlugTouched(true);
                    setField("slug", slugify(event.target.value));
                  }}
                  className="cms-input"
                />
              </label>
            </div>

            <label className="block text-sm font-semibold">
              Short excerpt
              <textarea
                rows={3}
                maxLength={500}
                value={form.excerpt}
                onChange={(event) => setField("excerpt", event.target.value)}
                className="cms-input"
              />
            </label>

            <div className="rounded-xl border border-slate-200 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <label className="text-sm font-semibold">Feature image</label>
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold hover:bg-slate-50">
                  <ImagePlus size={16} />
                  {uploading ? "Uploading..." : "Upload feature image"}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
                    className="hidden"
                    disabled={uploading}
                    onChange={(event) => {
                      uploadImages(event.target.files, true);
                      event.target.value = "";
                    }}
                  />
                </label>
              </div>

              <input
                value={form.featureImage}
                onChange={(event) => setField("featureImage", event.target.value)}
                placeholder="Or paste hosted image URL"
                className="cms-input"
              />

              {form.featureImage && (
                <img
                  src={imageUrl(form.featureImage)}
                  alt="Feature preview"
                  className="mt-3 max-h-56 rounded-xl border object-cover"
                />
              )}
            </div>

            <label className="block text-sm font-semibold">
              HTML article content *
              <textarea
                required
                rows={16}
                value={form.contentHtml}
                onChange={(event) => setField("contentHtml", event.target.value)}
                placeholder={'<h2>Your heading</h2>\n<p>Write your article HTML here...</p>\n<img src="/uploads/your-image.jpg" alt="Description">'}
                className="cms-input font-mono text-xs leading-6"
              />
              <span className="mt-1 block text-xs font-normal text-slate-500">
                HTML is supported. Script tags and unsafe attributes are removed on the server.
              </span>
            </label>

            <div className="rounded-xl border border-slate-200 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <label className="text-sm font-semibold">Additional article images</label>
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold hover:bg-slate-50">
                  <Upload size={16} />
                  {uploading ? "Uploading..." : "Upload images"}
                  <input
                    type="file"
                    multiple
                    accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
                    className="hidden"
                    disabled={uploading}
                    onChange={(event) => {
                      uploadImages(event.target.files, false);
                      event.target.value = "";
                    }}
                  />
                </label>
              </div>

              <textarea
                rows={3}
                value={form.imagesText}
                onChange={(event) => setField("imagesText", event.target.value)}
                placeholder="One hosted image URL per line"
                className="cms-input font-mono text-xs"
              />
              <p className="mt-1 text-xs font-normal text-slate-500">
                Each image can be up to 5 MB. Uploaded files are stored in the backend uploads folder.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block text-sm font-semibold">
                Tags (comma-separated)
                <input
                  value={form.tagsText}
                  onChange={(event) => setField("tagsText", event.target.value)}
                  placeholder="SEO, Web Development, Marketing"
                  className="cms-input"
                />
              </label>

              <label className="block text-sm font-semibold">
                SEO keywords (comma-separated)
                <input
                  value={form.keywordsText}
                  onChange={(event) => setField("keywordsText", event.target.value)}
                  placeholder="website development USA, SEO services"
                  className="cms-input"
                />
              </label>
            </div>

            <label className="block text-sm font-semibold">
              Page title / SEO title
              <input
                maxLength={180}
                value={form.pageTitle}
                onChange={(event) => setField("pageTitle", event.target.value)}
                placeholder="SEO title shown in browser and search results"
                className="cms-input"
              />
            </label>

            <label className="block text-sm font-semibold">
              Meta description
              <textarea
                rows={3}
                maxLength={320}
                value={form.metaDescription}
                onChange={(event) => setField("metaDescription", event.target.value)}
                placeholder="Write a clear search snippet for this article"
                className="cms-input"
              />
              <span className="mt-1 block text-xs font-normal text-slate-500">
                {form.metaDescription.length}/320 characters
              </span>
            </label>

            <div className="flex flex-col gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:items-center sm:justify-between">
              <label className="flex items-center gap-2 text-sm font-semibold">
                Publication status
                <select
                  value={form.status}
                  onChange={(event) => setField("status", event.target.value)}
                  className="rounded-lg border border-slate-200 px-3 py-2"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </label>

              <button
                disabled={busy || uploading}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand px-6 py-3 font-semibold text-white disabled:opacity-60"
              >
                <FilePlus2 size={17} />
                {busy
                  ? "Saving..."
                  : form.status === "published"
                    ? "Publish post"
                    : "Save draft"}
              </button>
            </div>
          </form>
        </section>

        <aside className="space-y-5">
          <section className="rounded-2xl border border-black/5 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-bold text-[hsl(var(--brand-navy))]">
              Your articles{" "}
              <span className="ml-1 text-sm font-normal text-slate-400">
                ({posts.length})
              </span>
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Edit drafts, update SEO fields, publish, or delete a post.
            </p>

            <div className="mt-4 space-y-3">
              {posts.length === 0 && (
                <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">
                  No articles yet. Create your first post.
                </p>
              )}

              {posts.map((post) => (
                <div key={post._id} className="rounded-xl border border-slate-100 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="break-words font-semibold text-[hsl(var(--brand-navy))]">
                        {post.title}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">/{post.slug}</p>
                    </div>
                    <span
                      className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-bold uppercase ${
                        post.status === "published"
                          ? "bg-green-50 text-green-700"
                          : "bg-amber-50 text-amber-700"
                      }`}
                    >
                      {post.status}
                    </span>
                  </div>

                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={() => editPost(post)}
                      className="inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-xs font-semibold"
                    >
                      <Pencil size={13} /> Edit
                    </button>

                    {post.status === "published" && (
                      <Link
                        to={`/blog/${post.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-lg border px-3 py-1.5 text-xs font-semibold"
                      >
                        View
                      </Link>
                    )}

                    <button
                      onClick={() => deletePost(post)}
                      className="inline-flex items-center gap-1 rounded-lg border border-red-100 px-3 py-1.5 text-xs font-semibold text-red-600"
                    >
                      <Trash2 size={13} /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-2xl bg-[hsl(var(--brand-navy))] p-5 text-white">
            <h3 className="font-bold">Publishing checklist</h3>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-5 text-white/75">
              <li>Add a descriptive page title and meta description.</li>
              <li>Set a feature image and useful alt text in HTML images.</li>
              <li>Use relevant tags and keywords.</li>
              <li>Preview the article before publishing.</li>
            </ul>
          </section>
        </aside>
      </main>

      <style>{`
        .cms-input {
          display: block;
          width: 100%;
          margin-top: .4rem;
          border: 1px solid #e2e8f0;
          border-radius: .75rem;
          padding: .75rem .9rem;
          font-weight: 400;
          outline: none;
          background: #fff;
        }
        .cms-input:focus {
          border-color: hsl(var(--brand-violet));
          box-shadow: 0 0 0 3px hsl(var(--brand-violet) / .1);
        }
      `}</style>
    </div>
  );
}
